import { useState, useEffect } from 'react';
import {
  TOPICS,
  eyebrowStyle, h1Style, h2Style, subtitleStyle, sectionLabelStyle,
  cardStyle,
} from './tokens';
import DiscussionCard from './DiscussionCard';
import { fetchTopicDiverseFeed, fetchExperienceCounts, humanizeError } from '../../lib/communityService';

const FEATURED_LIMIT = 3;

/**
 * CommunityHome — compact homepage v3.
 *
 * Section order:
 *   1. Community Hero  (title + Ask CTA)
 *   2. Browse Topics   (9 tinted outline-icon cards, always above any feed)
 *   3. Featured (3 mixed discussions across topics)   ← was Recent + Trending + Recommended
 *   4. Load more discussions → dedicated All-Discussions screen
 *
 * The homepage is a discovery surface, not a firehose. The three featured
 * cards are topic-diverse (one per topic when possible, filled with newest
 * within a jittered window otherwise) so repeat visits feel alive and no
 * single topic dominates. Depth lives in Topic pages and All Discussions.
 */
export default function CommunityHome({ onAsk, onOpenQuestion, onOpenTopic, onOpenAll }) {
  const [featured, setFeatured] = useState([]);
  const [counts,   setCounts]   = useState({});
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const rows = await fetchTopicDiverseFeed({ limit: FEATURED_LIMIT });
        if (cancelled) return;
        setFeatured(rows);
        if (rows.length) setCounts(await fetchExperienceCounts(rows.map(r => r.id)));
      } catch (e) {
        if (!cancelled) { setFeatured([]); setError(humanizeError(e)); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div style={{ padding: '20px 16px 40px' }}>
      {/* ─── 1. HERO ───────────────────────────────────────── */}
      <header style={{ padding: '12px 4px 20px' }} data-testid="community-hero">
        <div style={eyebrowStyle}>COMMUNITY</div>
        <h1 style={h1Style}>Community</h1>
        <p style={subtitleStyle}>Ask questions.<br/>Learn from experiences.<br/>Support one another.</p>
      </header>

      {/* Ask a Question — primary CTA under the hero. */}
      <button
        onClick={onAsk}
        data-testid="community-ask-cta"
        style={{
          width: '100%', textAlign: 'left', border: '1px solid rgba(109,94,245,.16)',
          cursor: 'pointer', padding: 20, marginBottom: 28, borderRadius: 22,
          background: 'linear-gradient(135deg, #F5F0FF, #FFFFFF 80%)',
          boxShadow: '0 10px 30px -12px rgba(109,94,245,.22)',
        }}
      >
        <div style={{ ...eyebrowStyle, color: '#6D5EF5' }}>ASK A QUESTION</div>
        <h2 style={{ ...h2Style, marginTop: 6 }}>Can&rsquo;t find an answer?</h2>
        <p style={{ ...subtitleStyle, marginTop: 6 }}>Ask the community.</p>
        <div style={{ marginTop: 12, color: '#6D5EF5', fontWeight: 600, fontSize: 13 }}>Start a question →</div>
      </button>

      {/* ─── 2. BROWSE TOPICS ─────────────────────────────── */}
      <div style={sectionLabelStyle}>Browse Topics</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 28 }} data-testid="topics-grid">
        {TOPICS.map((t) => {
          const Icon = t.Icon;
          return (
            <button
              key={t.key}
              onClick={() => onOpenTopic(t.key)}
              data-testid={`community-topic-${t.key}`}
              aria-label={`Open ${t.label}`}
              style={{
                padding: '14px 8px 12px', textAlign: 'center', cursor: 'pointer', fontFamily: 'inherit',
                background: t.tint,
                border: '1px solid rgba(0,0,0,.05)',
                borderRadius: 16,
                boxShadow: '0 2px 8px -4px rgba(14,15,19,.06)',
                transition: 'transform .15s ease, box-shadow .15s ease',
              }}
              onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.98)'}
              onMouseUp={(e)   => e.currentTarget.style.transform = ''}
              onMouseLeave={(e)=> e.currentTarget.style.transform = ''}
            >
              <Icon size={22} strokeWidth={1.5} color={t.stroke} style={{ display: 'block', margin: '0 auto 8px' }} />
              <div style={{ fontSize: 12.5, fontWeight: 600, color: '#232323', lineHeight: 1.2 }}>{t.label}</div>
            </button>
          );
        })}
      </div>

      {/* ─── 3. FEATURED (3 mixed discussions) ────────────── */}
      <section style={{ marginBottom: 20 }} data-testid="section-featured">
        <div style={sectionLabelStyle}>Recent Discussions</div>
        {loading ? (
          <div style={{ ...cardStyle, padding: '22px 20px', textAlign: 'center', color: '#8A847C', fontSize: 12.5 }}>Loading…</div>
        ) : error ? (
          <div style={{ ...cardStyle, padding: '22px 20px', textAlign: 'center', color: '#C67A69', fontSize: 12.5 }}>{error}</div>
        ) : featured.length === 0 ? (
          <div style={{ ...cardStyle, padding: '22px 20px', textAlign: 'center', color: '#8A847C', fontSize: 12.5 }}>
            Nothing yet — check back soon.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {featured.map((q) => (
              <DiscussionCard
                key={q.id}
                q={q}
                sharedCount={counts[q.id] || 0}
                onOpen={() => onOpenQuestion(q)}
              />
            ))}
          </div>
        )}
      </section>

      {/* ─── 4. LOAD MORE ─────────────────────────────────── */}
      <button
        onClick={onOpenAll}
        data-testid="community-load-more"
        style={{
          width: '100%', marginTop: 8, padding: '14px 20px', borderRadius: 999,
          background: '#fff', border: '1px solid rgba(109,94,245,.30)', color: '#6D5EF5',
          fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
          boxShadow: '0 2px 10px -6px rgba(109,94,245,.20)',
        }}
      >
        View all discussions →
      </button>
    </div>
  );
}
